import { PropsWithChildren, useEffect, useMemo } from 'react';
import { PX_PER_MINUTE } from '../constants';
import { CalendarGridStore, CalendarGridStoreProvider, useNonReactiveStore } from '../store/calendar-grid.store';
import { ZoomStore, ZoomStoreProvider, useNonReactiveZoomStore } from '../store/zoom.store';
import { CalendarColumn, CalendarColumnGroup, CalendarColumnHeaderType, CalendarEvent } from '../types';
import { convertToIdMap } from '../utils';
import { useZoomKeyboardShortcuts } from '../zoom/use-zoom-keyboard-shortcuts';
import type { ZoomConfig } from '../zoom/zoom-types';

type BaseCalendarGridProviderProps = {
  events: CalendarEvent[];
  columnGroups?: CalendarColumnGroup[];
  columnBodyVariant?: CalendarGridStore['columnBodyVariant'];
  pxPerMinute?: number;
  timeIndicatorLines?: CalendarGridStore['timeIndicatorLines'];
  onEventSelect?: CalendarGridStore['onEventSelect'];
  baseTimezone?: string;
  onColumnReorder?: (groupId: string, newColumnIds: string[]) => void;
  zoomConfig?: ZoomConfig;
};

type CalendarGridProviderPropsByHeaderType<THeader extends CalendarColumnHeaderType> = BaseCalendarGridProviderProps & {
  headerType: THeader;
  columns: CalendarColumn<THeader>[];
};

export type CalendarGridProviderProps = {
  [THeader in CalendarColumnHeaderType]: CalendarGridProviderPropsByHeaderType<THeader>;
}[CalendarColumnHeaderType];

export const CalendarGridProvider = ({
  children,
  headerType,
  columnBodyVariant = 'grid',
  events,
  columns,
  columnGroups,
  pxPerMinute = PX_PER_MINUTE,
  timeIndicatorLines = 'hourly',
  baseTimezone,
  onColumnReorder,
  onEventSelect,
  zoomConfig,
}: PropsWithChildren<CalendarGridProviderProps>) => {
  const initialState = useMemo(() => {
    let gridColumnGroups = columnGroups;
    if (!columnGroups?.length) {
      gridColumnGroups = [
        {
          id: 'default-group',
          name: '',
          columnIds: columns.map((column) => column.id),
          groupOffset: 0,
          timezone: baseTimezone,
        },
      ];
    }

    const groupIds = gridColumnGroups?.map((group) => group.id) || [];
    const eventsById = convertToIdMap(events);
    const columnsById = convertToIdMap<CalendarColumn>(columns);
    const groupsById = convertToIdMap(gridColumnGroups || []);

    return {
      headerType,
      groupIds,
      groupsById,
      columnsById,
      eventsById,
      timeIndicatorLines: headerType === 'weekday' ? 'none' : timeIndicatorLines, // time indicator lines are only applicable for non-date-based columns
      hasMultipleGroups: groupIds.length > 1,
      columnBodyVariant,
      isColumnReorderEnabled: !!onColumnReorder,
      onColumnReorder: onColumnReorder,
      onEventSelect,
    };
  }, [
    columnGroups,
    events,
    columns,
    headerType,
    timeIndicatorLines,
    columnBodyVariant,
    onColumnReorder,
    onEventSelect,
    baseTimezone,
  ]);

  const zoomInitialState = useMemo<Partial<ZoomStore>>(() => {
    if (!zoomConfig?.enabled) return { pxPerMinute };

    const zoomLevel = zoomConfig.zoomLevel ?? zoomConfig.defaultLevel ?? 1;
    const zoomLevels = zoomConfig.levels ? [...zoomConfig.levels].sort((a, b) => a - b) : [1, 2];
    return {
      zoomEnabled: true,
      showControls: zoomConfig.showControls ?? true,
      zoomLevel,
      zoomLevels,
      pxPerMinute: Math.round(PX_PER_MINUTE * zoomLevel),
      canZoomIn: zoomLevel > zoomLevels[0],
      canZoomOut: zoomLevel < zoomLevels[zoomLevels.length - 1],
      onZoomLevelChange: zoomConfig.onZoomLevelChange,
    };
  }, [pxPerMinute, zoomConfig]);

  return (
    <CalendarGridStoreProvider initialState={initialState}>
      {/* // TODO: this is a workaround to sync the initial state to the store. We should ideally not need this
         // and directly use the initialState in the provider, but that requires some changes
        // in our createProvider util to support dynamic initial state. */}
      <CalendarGridStoreSync state={initialState} />
      <ZoomStoreProvider initialState={zoomInitialState}>
        <ZoomStoreSync state={zoomInitialState} />
        {zoomConfig?.enabled && <ZoomKeyboardShortcutsActivator />}
        {children}
      </ZoomStoreProvider>
    </CalendarGridStoreProvider>
  );
};

const ZoomKeyboardShortcutsActivator = () => {
  useZoomKeyboardShortcuts();
  return null;
};

const CalendarGridStoreSync = ({ state }: { state: Partial<CalendarGridStore> }) => {
  const store = useNonReactiveStore();

  useEffect(() => {
    store.setState(state);
  }, [state, store]);

  return null;
};

const ZoomStoreSync = ({ state }: { state: Partial<ZoomStore> }) => {
  const store = useNonReactiveZoomStore();

  useEffect(() => {
    store.setState(state);
  }, [state, store]);

  return null;
};
